import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { resetPassword } from '../../services/auth';
import { Lock, ArrowLeft, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');
    try {
      await resetPassword(token, newPassword);
      setMessage('Password reset successful. Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // If token is missing, show a simple error page (kept consistent with design)
  if (!token && error) {
    return (
      <div className="auth-split-container flex flex-col md:flex-row min-h-screen bg-white">
        <div className="auth-sidebar hidden md:flex md:w-2/5 bg-blue-700 bg-gradient-to-br from-blue-800 to-blue-600 text-white p-10 flex-col justify-between">
          <div>
            <div className="brand-badge inline-block bg-white/20 backdrop-blur-sm px-4 py-1 rounded-full text-xs font-bold tracking-wider mb-6">
              NYM Portal
            </div>
            <h1 className="text-4xl font-bold leading-tight">Invalid Token</h1>
            <p className="text-white/80 mt-4 text-lg">
              The reset link you used is invalid or has expired.
            </p>
          </div>
          <div className="auth-sidebar-footer text-xs opacity-50">
            © 2026 Friends Church (Quakers) NYM
          </div>
        </div>
        <div className="auth-form-section flex-1 bg-white p-6 md:p-12 flex items-center justify-center">
          <div className="login-form-wrapper w-full max-w-md mx-auto py-8 md:py-0">
            <div className="md:hidden flex justify-center mb-6">
              <span className="text-2xl font-black text-blue-700">NYM</span>
            </div>
            <h2 className="text-3xl font-black text-gray-900 mb-2 text-center md:text-left">Invalid Token</h2>
            <p className="text-gray-500 mb-8 text-center md:text-left">{error}</p>
            <Link
              to="/forgot-password"
              className="inline-flex items-center gap-2 text-blue-600 font-bold hover:underline transition-colors"
            >
              <ArrowLeft size={16} />
              Request new reset link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-split-container flex flex-col md:flex-row min-h-screen bg-white">
      {/* LEFT SIDE: Brand & Welcome – SOLID BLUE */}
      <div className="auth-sidebar hidden md:flex md:w-2/5 bg-blue-700 bg-gradient-to-br from-blue-800 to-blue-600 text-white p-10 flex-col justify-between">
        <div>
          <div className="brand-badge inline-block bg-white/20 backdrop-blur-sm px-4 py-1 rounded-full text-xs font-bold tracking-wider mb-6">
            NYM Portal
          </div>
          <h1 className="text-4xl font-bold leading-tight">Set New Password</h1>
          <p className="text-white/80 mt-4 text-lg">
            Enter your new password below. Make sure it's at least 6 characters long.
          </p>
        </div>
        <div className="auth-sidebar-footer text-xs opacity-50">
          © 2026 Friends Church (Quakers) NYM
        </div>
      </div>

      {/* RIGHT SIDE: The Form */}
      <div className="auth-form-section flex-1 bg-white p-6 md:p-12 flex items-center justify-center">
        <div className="login-form-wrapper w-full max-w-md mx-auto py-8 md:py-0">
          {/* Mobile logo */}
          <div className="md:hidden flex justify-center mb-6">
            <span className="text-2xl font-black text-blue-700">NYM</span>
          </div>

          <h2 className="text-3xl font-black text-gray-900 mb-2 text-center md:text-left">
            Reset Password
          </h2>
          <p className="text-gray-500 mb-8 text-center md:text-left">
            Enter your new password below.
          </p>

          {message && (
            <div className="mb-6 flex items-start gap-3 p-4 bg-emerald-50 text-emerald-700 rounded-xl border border-emerald-100">
              <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm font-medium">{message}</span>
            </div>
          )}

          {error && (
            <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 text-red-700 rounded-xl border border-red-100">
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="modern-form space-y-6">
            {/* New Password */}
            <div className="input-group">
              <label className="block text-sm font-bold text-gray-700 mb-1">New Password</label>
              <div className="input-wrapper relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="password"
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Confirm Password */}
            <div className="input-group">
              <label className="block text-sm font-bold text-gray-700 mb-1">Confirm Password</label>
              <div className="input-wrapper relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="password"
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  <span>Resetting...</span>
                </>
              ) : (
                <span>Reset Password</span>
              )}
            </button>
          </form>

          <div className="text-center mt-8 text-sm text-gray-500">
            <Link to="/login" className="inline-flex items-center gap-1 text-blue-600 font-bold hover:underline transition-colors">
              <ArrowLeft size={16} />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;